import React, { useState, useRef } from "react";
import { EyeIcon } from "@heroicons/react/solid";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import useGet from "../../hooks/read/useGet";
import Pagination from "../../components/pagination";
import Loader from "../../components/Loader";
import AttendanceModal from "../../components/attendance/AttendanceModal";
import { debounce } from "lodash";

const AttendanceByYear = () => {
  const navigate = useNavigate();
  const [financialYears, setFinancialYears] = useState([]);
  const [selectedFinancialYearId, setSelectedFinancialYearId] = useState(null);
  const [isImportModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch financial years list
  useGet({
    url: "financial_year_list",
    initialData: { pageNo: 0, limit: 100 },
    onSuccess: (data) => {
      if (data?.data) {
        setFinancialYears(data.data);
        const currentYear = data.data.find((year) => year.current_year);
        if (currentYear) {
          setSelectedFinancialYearId(currentYear.financial_year_id);
          // Set the attendance list params with the current year
          setGetListParams((prev) => ({
            ...prev,
            financial_year_id: currentYear.financial_year_id,
            pageNo: 0,
            searchQuery: "",
          }));
        }
      }
    },
    onError: () => {
      toast.error("Failed to fetch financial years");
    },
  });

  // Fetch yearly attendance list
  const {
    list: attendanceList,
    paramsObject,
    setGetListParams,
  } = useGet({
    url: "employee_yearly_attendance_list",
    initialData: {
      pageNo: 0,
      limit: 10,
      searchQuery: "",
      financial_year_id: "",
    },
    enabled: !!selectedFinancialYearId,
    onError: () => {
      toast.error("Failed to fetch attendance data");
    },
  });

  // Create debounced search function
  const debouncedOnSearch = useRef(
    debounce((val) => {
      setGetListParams((prev) => ({
        ...prev,
        searchQuery: val,
        pageNo: 0,
      }));
    }, 1000)
  ).current;

  const handlePageClick = (val) => {
    setGetListParams((prev) => ({
      ...prev,
      pageNo: val.selected,
    }));
  };

  const handleFinancialYearChange = (e) => {
    const newId = e.target.value;
    setSelectedFinancialYearId(newId);
    setGetListParams((prev) => ({
      ...prev,
      financial_year_id: newId,
      pageNo: 0,
      searchQuery: "",
    }));
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    debouncedOnSearch(value);
  };

  const handleViewAttendance = (financialYearId, userId) => {
    navigate(`/view-monthly-attendance/${financialYearId}/${userId}`);
  };

  return (
    <div className="px-4">
      <div className="flex flex-col gap-3 mb-6">
        <h1 className="text-2xl font-semibold roboto">
          Attendance by Year
        </h1>
        <div className="flex items-center space-x-4">
          <div className="w-64">
            <select
              value={selectedFinancialYearId || ""}
              onChange={handleFinancialYearChange}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-color1"
            >
              <option value="">Select Financial Year</option>
              {financialYears.map((year) => (
                <option
                  key={year.financial_year_id}
                  value={year.financial_year_id}
                >
                  {year.name}
                </option>
              ))}
            </select>
          </div>
          <div className="w-64">
            <input
              type="search"
              placeholder="Search by name"
              value={searchQuery}
              onChange={handleSearch}
              className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-color1"
            />
          </div>
          <div className="flex items-center space-x-3 ml-auto">
            <button
              type="button"
              onClick={() => setIsModalOpen(true)}
              className="border border-gray-600 rounded px-4 py-2 text-xs"
            >
              Import Attendance
            </button>
          </div>
        </div>
      </div>

      {attendanceList.isLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader className="h-8 w-8 text-color1" />
        </div>
      ) : attendanceList.data?.data?.length <= 0 ? (
        <div className="text-center my-10">
          <p className="text-gray-700 text-3xl">No Data To Show</p>
        </div>
      ) : (
        <>
          <div className="max-w-full overflow-x-auto">
            <table className="table-auto w-full">
              <thead>
                <tr className="bg-[color:var(--color1)] text-center">
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4 border-l border-transparent">
                    Employee Name
                  </th>
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4">
                    Employee Code
                  </th>
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4">
                    Present Days
                  </th>
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4">
                    Absent Days
                  </th>
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4">
                    Total Leaves
                  </th>
                  <th className="w-1/6 min-w-[160px] text-lg font-semibold text-white py-3 px-3 lg:px-4">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {attendanceList.data?.data?.map((data, index) => (
                  <tr key={index}>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-l border-[#E8E8E8]">
                      {data.employee_name}
                    </td>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.employee_code}
                    </td>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.total_present_days}
                    </td>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.total_absent_days}
                    </td>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      {data.total_leaves}
                    </td>
                    <td className="text-center text-dark font-medium text-sm py-3 px-2 bg-[#F3F6FF] border-b border-[#E8E8E8]">
                      <button
                        onClick={() =>
                          handleViewAttendance(
                            selectedFinancialYearId,
                            data.user_id
                          )
                        }
                        className="bg-[color:var(--color1)] text-white rounded px-4 py-2 flex items-center space-x-2 justify-center mx-auto"
                      >
                        <EyeIcon className="w-5 h-5" />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-5 mb-10">
            <Pagination
              currentPage={paramsObject.pageNo}
              lengthofItems={attendanceList.data?.count}
              limit={10}
              onPageChange={handlePageClick}
            />
          </div>
        </>
      )}

      <AttendanceModal
        open={isImportModalOpen}
        handleClose={() => setIsModalOpen(false)}
        sampleDownloadEndpoint="monthly_attendance_count_template_excel_download"
        uploadType="monthly"
      />
    </div>
  );
};

export default AttendanceByYear;
